import React from 'react';
import {IconButton, IContextualMenuItem} from '@fluentui/react';
import {toast} from 'react-toastify';
import {humanizeRecordType, IRecordLink, useLazyResolveRecordUrlQuery} from '../../store/endpoints/RecordLinksEndpoints';

interface IOpenInCrmMenuProps {
    /** CRM/PSA record links for the entity (from a getXLinks query). When empty the menu renders nothing. */
    links?: IRecordLink[];
    /** Extra menu items to append (e.g. "Link with {CRM}" actions for unlinked folios). The menu renders
     * when there are either links or extra items. */
    extraItems?: IContextualMenuItem[];
    /** Optional override for the overflow icon. */
    iconName?: string;
    title?: string;
}

/**
 * A three-dots overflow button that lists "Open in {folio}" actions for an entity's CRM/PSA record links,
 * resolving the live deep-link URL on click. Mirrors the menu in ProcureDemandPanel / ContactDetailsModal.
 * Renders nothing when there are no links.
 */
const OpenInCrmMenu: React.FC<IOpenInCrmMenuProps> = (props) => {
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();

    const openRecord = async (link: IRecordLink) => {
        try {
            const res = await resolveRecordUrl({folioId: link.folioId, recordType: link.recordType, recordId: link.recordId}).unwrap();
            if (res.supported && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(`This record can't be opened in ${link.folioName ?? 'the CRM'}`);
            }
        } catch {
            toast.error('Failed to open record');
        }
    };

    const linkItems: IContextualMenuItem[] = (props.links ?? []).map(link => ({
        key: `${link.folioId}-${link.recordType}-${link.recordId}`,
        text: `Open in ${link.folioName ?? 'CRM'}`,
        iconProps: {iconName: 'OpenInNewWindow'},
        disabled: !link.supported,
        title: link.supported ? undefined : `${link.folioName ?? 'This folio'} does not support direct linking to ${humanizeRecordType(link.recordType)}`,
        onClick: link.supported ? () => { void openRecord(link); } : undefined
    }));

    const items: IContextualMenuItem[] = [...linkItems, ...(props.extraItems ?? [])];
    if (items.length === 0) return null;

    return (
        <IconButton
            iconProps={{iconName: props.iconName ?? 'MoreVertical'}}
            title={props.title ?? 'More actions'}
            ariaLabel={props.title ?? 'More actions'}
            onRenderMenuIcon={() => null}
            menuProps={{items}}
        />
    );
};

export default OpenInCrmMenu;
